import React, { useCallback } from 'react';
import { ActionsBar, Button, Column, Row } from '@ydistri/ds';
import { useSelector } from 'react-redux';
import { ApiOperationType, EntityIdentificationType } from '@ydistri/api-sdk';
import LoadingIndicator from '../../../../../components/global/LoadingIndicator/LoadingIndicator';
import { ReduxState, useAppDispatch } from '../../../../../store';
import {
  resetEntityListItems,
  setProductIdentificationType,
  setStoreIdentificationType,
} from '../../entityListAdministrationSlice';
import useImportItems from '../../hooks/useImportItems';
import EntityListItemsArea from './EntityListItemsArea';
import {
  EntityListContentItem,
  EntityListTypeSupported,
  getEntityListSpecification,
  IMPORT_STEPS,
} from '../../entityListsLib';
import EntityIdentificationTypeComponent from '../../../common/EntityIdentificationTypeComponent';
import { useCurrentEntityListState } from '../../hooks/useCurrentEntityListState';
import EntityListImportParsingResultOverview from './parsing_results_overview/EntityListImportParsingResultOverview';
import useParseAndUpdateEntityListItems from '../../hooks/useParseAndUpdateEntityListItems';
import ImportWaitingHint from '../../components/ImportWaitingHint';

interface EntityListEditorProps {
  entityListType: EntityListTypeSupported;
}

const EntityListEditor: React.FC<EntityListEditorProps> = ({ entityListType }) => {
  const specificationContentItems =
    getEntityListSpecification(entityListType).getContentTableStructure();
  const entityListState = useCurrentEntityListState(entityListType);

  const storeIdentificationType =
    entityListState?.storeIdentificationType ?? EntityIdentificationType.CustomerId;
  const productIdentificationType =
    entityListState?.productIdentificationType ?? EntityIdentificationType.CustomerId;
  const isLoading = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].isLoading,
  );

  let isRunning = false;
  if (
    entityListState?.importStepRunning[IMPORT_STEPS.ITEM_IMPORT] ||
    entityListState?.importStepRunning[IMPORT_STEPS.ITEM_VALIDATION]
  ) {
    isRunning = true;
  }

  const productItemsContent = entityListState?.productItemsContent ?? '';

  const selectedEntityList = useSelector(
    (state: ReduxState) => state.entityListsAdministration[entityListType].selectedEntityList,
  );

  const dispatch = useAppDispatch();

  const { callback: importItems, isPending } = useImportItems(entityListType);

  const parseAndUpdateItems = useParseAndUpdateEntityListItems(entityListType);

  const selectWarehouseIdentificationHandler = useCallback(
    (newValue: EntityIdentificationType) => {
      dispatch(setStoreIdentificationType({ entityListType, data: newValue }));
      dispatch(resetEntityListItems({ entityListType }));
      if (productItemsContent) {
        parseAndUpdateItems(productItemsContent, productIdentificationType, newValue);
      }
    },
    [dispatch, entityListType, productItemsContent, parseAndUpdateItems, productIdentificationType],
  );

  const selectProductIdentificationHandler = useCallback(
    (newValue: EntityIdentificationType) => {
      dispatch(setProductIdentificationType({ entityListType, data: newValue }));
      dispatch(resetEntityListItems({ entityListType }));
      if (productItemsContent) {
        parseAndUpdateItems(productItemsContent, newValue, storeIdentificationType);
      }
    },
    [dispatch, entityListType, productItemsContent, parseAndUpdateItems, storeIdentificationType],
  );

  const replaceItems = useCallback(() => {
    importItems(ApiOperationType.Replace);
  }, [importItems]);

  const addItems = useCallback(() => {
    importItems(ApiOperationType.Patch);
  }, [importItems]);

  if (!selectedEntityList) {
    return null;
  }

  const isEntityListEmpty = selectedEntityList.productCount === 0;

  if (isLoading) {
    return (
      <Column $gap="0.5rem" $minWidth="45rem">
        <LoadingIndicator text="Validating..." />
      </Column>
    );
  } else {
    return (
      <Row $flexWrap="nowrap" $flexGrow={1} $gap="2rem" data-testid="EntityListEditor">
        <Column $gap="1rem" $width="450px">
          {specificationContentItems.includes(EntityListContentItem.PRODUCT) && (
            <EntityIdentificationTypeComponent
              type="Product"
              value={productIdentificationType}
              onChange={selectProductIdentificationHandler}
              id="ProductIdentificationTypeSelect"
              disabled={isRunning}
            />
          )}
          {specificationContentItems.includes(EntityListContentItem.STORE) && (
            <EntityIdentificationTypeComponent
              type="Store"
              value={storeIdentificationType}
              onChange={selectWarehouseIdentificationHandler}
              id="StoreIdentificationTypeSelect"
              disabled={isRunning}
            />
          )}
          <EntityListItemsArea label="Items" entityListType={entityListType} disabled={isRunning} />
          <ActionsBar data-testid="EntityListEditorActions">
            <Button
              disabled={
                productItemsContent.length === 0 ||
                entityListState?.importType === ApiOperationType.Replace
              }
              data-testid="add-to-entity-list"
              type="primary"
              onClick={addItems}
              loading={isPending}
            >
              Add to {getEntityListSpecification(entityListType).title}...
            </Button>
            {!isEntityListEmpty && (
              <Button
                disabled={
                  productItemsContent.length === 0 ||
                  entityListState?.importType === ApiOperationType.Patch
                }
                data-testid="replace-entity-list"
                type="primary"
                onClick={replaceItems}
                loading={isPending}
              >
                Replace {getEntityListSpecification(entityListType).title}...
              </Button>
            )}
          </ActionsBar>
          <ImportWaitingHint pending={isPending} />
        </Column>
        <Column $flexGrow={1}>
          <Column $flexGrow={1}>
            <EntityListImportParsingResultOverview entityListType={entityListType} />
          </Column>
        </Column>
      </Row>
    );
  }
};

export default EntityListEditor;
